import { RefObject, useEffect, useState } from 'react';

const defaultOptions = {
  threshold: 0,
  root: null,
  rootMargin: '0px',
};

type MyIntersectionObserverEntryT = {
  isIntersecting: boolean;
};

function useIntersectionObserver(
  targetOrRef: RefObject<Element> | Element | null,
  options: IntersectionObserverInit = defaultOptions,
  activated = true,
  fireOnDisappear = false,
): MyIntersectionObserverEntryT | undefined {
  const [entry, setEntry] = useState<MyIntersectionObserverEntryT>();
  const isIntersecting = entry?.isIntersecting;
  const updateEntry = (entries: IntersectionObserverEntry[]): void => {
    const [ioEntry] = entries;
    if (!ioEntry) return;

    const next = ioEntry.isIntersecting;

    // 1) skip the very first false (keep entry undefined until true)
    if (!next && entry === undefined) return;

    // 2) update only if the boolean actually changes
    if (entry?.isIntersecting !== next) {
      setEntry({ isIntersecting: next });
    }
  };

  useEffect(() => {
    // Determine if targetOrRef is a RefObject or a direct Element
    const target =
      targetOrRef instanceof Element ? targetOrRef : targetOrRef?.current;

    // 처음부터 activated를 받아 필요한 것만 observe하게 한다.
    if (!activated) return;
    if (!target) return;

    // isIntersecting 에서 리턴하면 한번 작동하고 끝난다.
    if (isIntersecting && !fireOnDisappear) return;

    const observer = new IntersectionObserver(updateEntry, options);

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [
    targetOrRef,
    options.root,
    options.rootMargin,
    options.threshold,
    isIntersecting,
    activated,
    fireOnDisappear,
  ]);

  return entry;
}

export default useIntersectionObserver;
